/**
 * Gstr1View.tsx — GSTR-1 Outward Supplies Return
 * Tabs: B2B (4A), CDNR/CDNUR (9B), B2CS (7), B2CL (5), Nil/Exempt (8), HSN Summary (12)
 */
import { useState, useMemo } from 'react';
import { useStore } from '../store/useStore';
import { db } from '../db/db';
import { useLiveQuery } from 'dexie-react-hooks';
import { getStateCode } from '../lib/gstValidation';

const fmt = (n: number) =>
  n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const B2CL_THRESHOLD = 250000;
type TabType = 'b2b' | 'cdnr' | 'b2cs' | 'b2cl' | 'nil' | 'hsn';

export const Gstr1View = ({ onQuit }: any) => {
  const activeCompany = useStore((s) => s.activeCompany);
  const fromDate      = useStore((s) => s.fromDate);
  const toDate        = useStore((s) => s.toDate);
  const [tab, setTab] = useState<TabType>('b2b');

  const reportData = useLiveQuery(async () => {
    if (!activeCompany) return null;
    const salesTypes = ['Sales', 'Credit Note', 'Debit Note'];
    const vouchers = await db.vouchers
      .where('companyId').equals(activeCompany.id!)
      .filter(v => salesTypes.includes(v.type) && v.date >= fromDate && v.date <= toDate && !v.deleted)
      .toArray();

    const ledgers    = await db.ledgers.where('companyId').equals(activeCompany.id!).toArray();
    const ledgerMap  = new Map(ledgers.map(l => [l.id!, l]));
    const groups     = await db.groups.where('companyId').equals(activeCompany.id!).toArray();
    const groupMap   = new Map(groups.map(g => [g.id!, g.name]));
    const stockItems = await db.stockItems.where('companyId').equals(activeCompany.id!).toArray();
    const stockItemMap = new Map(stockItems.map(s => [s.id!, s]));
    const units      = await db.units.where('companyId').equals(activeCompany.id!).toArray();
    const unitMap    = new Map(units.map(u => [u.id!, u]));

    const invEntries = await db.inventoryEntries
      .where('voucherId').anyOf(vouchers.map(v => v.id!)).toArray();
    const invByVoucher = new Map<number, typeof invEntries>();
    invEntries.forEach(ie => {
      if (!invByVoucher.has(ie.voucherId)) invByVoucher.set(ie.voucherId, []);
      invByVoucher.get(ie.voucherId)!.push(ie);
    });

    const entries = await db.voucherEntries
      .where('voucherId').anyOf(vouchers.map(v => v.id!)).toArray();
    const entriesByVoucher = new Map<number, typeof entries>();
    entries.forEach(e => {
      if (!entriesByVoucher.has(e.voucherId)) entriesByVoucher.set(e.voucherId, []);
      entriesByVoucher.get(e.voucherId)!.push(e);
    });

    const companyStateCode = getStateCode((activeCompany as any).gstin, (activeCompany as any).state);

    return vouchers.map(v => {
      const vEntries = entriesByVoucher.get(v.id!) || [];
      const vInv     = invByVoucher.get(v.id!) || [];

      const partyEntry = vEntries.find(e => {
        const l = ledgerMap.get(e.ledgerId);
        if (!l) return false;
        const grp = l.groupId ? groupMap.get(l.groupId) : '';
        return grp && ['Sundry Debtors', 'Sundry Creditors', 'Cash-in-Hand', 'Bank Accounts'].includes(grp);
      });
      const partyLedger = partyEntry ? ledgerMap.get(partyEntry.ledgerId) : null;
      const partyStateCode = getStateCode(partyLedger?.gstin, partyLedger?.state);
      const placeOfSupply  = partyStateCode || companyStateCode;
      const isInterState   = !!(partyStateCode && companyStateCode && partyStateCode !== companyStateCode);

      let taxable = 0, cgst = 0, sgst = 0, igst = 0, cess = 0;
      vEntries.forEach(e => {
        const l = ledgerMap.get(e.ledgerId);
        if (!l) return;
        const amt = e.amount || 0;
        const grp = l.groupId ? groupMap.get(l.groupId) : '';
        if (l.name === 'CGST') cgst += amt;
        else if (l.name === 'SGST') sgst += amt;
        else if (l.name === 'IGST') igst += amt;
        else if (l.name === 'CESS') cess += amt;
        else if (['Sales Accounts', 'Direct Incomes', 'Indirect Incomes'].includes(grp || '')) taxable += amt;
      });

      const hsnItems = vInv.map(ie => {
        const si   = ie.stockItemId ? stockItemMap.get(ie.stockItemId) : null;
        const unit = si?.unitId ? unitMap.get(si.unitId) : null;
        return {
          hsn: si?.hsn || '', name: si?.name || '',
          qty: ie.quantity || 0, rate: ie.rate || 0,
          unit: unit?.symbol || 'Nos', gstRate: si?.gstRate ?? 0,
          taxable: (ie.quantity || 0) * (ie.rate || 0),
        };
      });

      return {
        date: v.date, voucherNumber: v.voucherNumber, referenceNumber: v.referenceNumber,
        type: v.type, party: partyLedger?.name || 'Unknown',
        gstin: partyLedger?.gstin || '', state: partyLedger?.state || '',
        placeOfSupply, isInterState,
        taxable, cgst, sgst, igst, cess,
        total: taxable + cgst + sgst + igst + cess,
        hsnItems, isB2B: !!(partyLedger?.gstin),
        isCreditNote: v.type === 'Credit Note',
        isDebitNote:  v.type === 'Debit Note',
        isNilExempt:  taxable > 0 && (cgst + sgst + igst) === 0,
      };
    });
  }, [activeCompany, fromDate, toDate]);

  const normalRows = useMemo(() => (reportData || []).filter(r => !r.isCreditNote && !r.isDebitNote && !r.isNilExempt), [reportData]);
  const b2bRows    = useMemo(() => normalRows.filter(r => r.isB2B), [normalRows]);
  const b2cRows    = useMemo(() => normalRows.filter(r => !r.isB2B), [normalRows]);
  const b2clRows   = useMemo(() => b2cRows.filter(r => r.isInterState && r.total > B2CL_THRESHOLD), [b2cRows]);
  const b2csRows   = useMemo(() => b2cRows.filter(r => !r.isInterState || r.total <= B2CL_THRESHOLD), [b2cRows]);
  const cdnrRows   = useMemo(() => (reportData || []).filter(r => (r.isCreditNote || r.isDebitNote) && !r.isNilExempt), [reportData]);
  const nilRows    = useMemo(() => (reportData || []).filter(r => r.isNilExempt), [reportData]);

  const hsnSummary = useMemo(() => {
    const map = new Map<string, { hsn: string; name: string; uqc: string; qty: number; taxable: number; cgst: number; sgst: number; igst: number; cess: number; gstRate: number }>();
    (reportData || []).forEach(row => {
      row.hsnItems.forEach((h: any) => {
        if (!h.hsn && !h.name) return;
        const key = h.hsn || h.name;
        if (!map.has(key)) map.set(key, { hsn: h.hsn, name: h.name, uqc: h.unit, qty: 0, taxable: 0, cgst: 0, sgst: 0, igst: 0, cess: 0, gstRate: h.gstRate });
        const entry = map.get(key)!;
        entry.qty += h.qty;
        entry.taxable += h.taxable;
        if (row.taxable > 0 && h.taxable > 0) {
          const share = h.taxable / row.taxable;
          entry.cgst += row.cgst * share;
          entry.sgst += row.sgst * share;
          entry.igst += row.igst * share;
          entry.cess += row.cess * share;
        }
      });
    });
    return Array.from(map.values()).sort((a, b) => b.taxable - a.taxable);
  }, [reportData]);

  const totals = useMemo(() => ({
    taxable: (reportData || []).reduce((s, r) => s + r.taxable, 0),
    cgst:    (reportData || []).reduce((s, r) => s + r.cgst,    0),
    sgst:    (reportData || []).reduce((s, r) => s + r.sgst,    0),
    igst:    (reportData || []).reduce((s, r) => s + r.igst,    0),
    cess:    (reportData || []).reduce((s, r) => s + r.cess,    0),
    total:   (reportData || []).reduce((s, r) => s + r.total,   0),
  }), [reportData]);

  const T = (t: string) =>
    `px-3 py-1 font-bold border-r border-[#aebec8] cursor-pointer text-[11px] ${tab === t ? 'bg-[#1e8585] text-white' : 'bg-white text-black hover:bg-[#e8f4f4]'}`;
  const th = 'px-2 py-1 text-left font-bold text-black border-b border-[#aebec8] whitespace-nowrap text-[11px]';
  const td = 'px-2 py-[2px] border-b border-gray-200 whitespace-nowrap text-[11px]';
  const tR = `${td} text-right tabular-nums`;

  const TAB_HINTS: Record<TabType, string> = {
    b2b:  'Table 4A — Invoice-wise outward supplies to GST-registered parties (GSTIN required)',
    cdnr: 'Table 9B — Credit/Debit notes issued to registered (CDNR) and unregistered (CDNUR) parties',
    b2cs: 'Table 7 — B2C supplies (intra-state OR interstate ≤₹2.5L per invoice)',
    b2cl: 'Table 5 — B2C interstate supplies exceeding ₹2,50,000 per invoice (invoice-wise)',
    nil:  'Table 8 — Nil-rated, exempt, and non-GST outward supplies (0% tax)',
    hsn:  'Table 12 — HSN-wise summary with UQC, quantity, taxable value and tax breakup',
  };

  if (!activeCompany) return null;

  const InvoiceTable = ({ rows, showGstin = false, showType = false, showPos = false }: any) => (
    <table className="w-full border-collapse">
      <thead className="sticky top-0 bg-white z-10">
        <tr>
          <th className={th}>Date</th>
          <th className={th}>Invoice No.</th>
          {showType && <th className={th}>Type</th>}
          <th className={th}>Party</th>
          {showGstin && <th className={th}>GSTIN</th>}
          {showPos   && <th className={th}>POS</th>}
          <th className={`${th} text-right`}>Taxable (₹)</th>
          <th className={`${th} text-right`}>CGST (₹)</th>
          <th className={`${th} text-right`}>SGST (₹)</th>
          <th className={`${th} text-right`}>IGST (₹)</th>
          {totals.cess > 0 && <th className={`${th} text-right`}>CESS (₹)</th>}
          <th className={`${th} text-right`}>Total (₹)</th>
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 ? (
          <tr><td colSpan={12} className="text-center text-gray-400 py-8 text-sm">No records for this period.</td></tr>
        ) : rows.map((r: any, i: number) => (
          <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-[#f8fcfc]'}>
            <td className={td}>{r.date}</td>
            <td className={td}>{r.referenceNumber || r.voucherNumber || '—'}</td>
            {showType && (
              <td className={td}>
                <span className={`text-[9px] px-1 py-0.5 rounded font-medium ${r.isCreditNote ? 'bg-orange-50 text-orange-700' : r.isDebitNote ? 'bg-purple-50 text-purple-700' : 'bg-teal-50 text-teal-700'}`}>
                  {r.isCreditNote ? 'CR NOTE' : r.isDebitNote ? 'DB NOTE' : 'INV'}
                </span>
              </td>
            )}
            <td className={td}>{r.party}</td>
            {showGstin && <td className={`${td} font-mono text-[10px]`}>{r.gstin || '—'}</td>}
            {showPos   && <td className={`${td} font-mono`}>{r.placeOfSupply || '—'}</td>}
            <td className={tR}>{fmt(r.taxable)}</td>
            <td className={tR}>{r.cgst > 0 ? fmt(r.cgst) : '—'}</td>
            <td className={tR}>{r.sgst > 0 ? fmt(r.sgst) : '—'}</td>
            <td className={tR}>{r.igst > 0 ? fmt(r.igst) : '—'}</td>
            {totals.cess > 0 && <td className={tR}>{r.cess > 0 ? fmt(r.cess) : '—'}</td>}
            <td className={`${tR} font-semibold`}>{fmt(r.total)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  return (
    <div className="flex flex-col w-full h-full bg-white z-20 absolute inset-0 font-sans text-[12px] text-black">
      <div className="bg-[#1e8585] text-white flex justify-between px-2 py-[2px] font-bold shadow-sm">
        <span className="w-1/3">GSTR-1 — Outward Supplies</span>
        <span className="w-1/3 text-center">{activeCompany.name}</span>
        <span className="w-1/3 text-right cursor-pointer hover:text-gray-300" onClick={onQuit}>✕</span>
      </div>

      <div className="bg-white flex items-center justify-between px-2 py-1 font-bold border-b border-[#aebec8] shadow-sm shrink-0">
        <span className="text-[11px]">Period: {fromDate} → {toDate}</span>
        {(activeCompany as any).gstin && (
          <span className="text-[10px] font-mono text-gray-500 border border-gray-200 px-2 py-[1px] rounded">
            GSTIN: {(activeCompany as any).gstin}
          </span>
        )}
      </div>

      <div className="grid grid-cols-6 gap-px bg-[#aebec8] border-b border-[#aebec8] shrink-0">
        {[
          ['Taxable', totals.taxable], ['CGST', totals.cgst], ['SGST', totals.sgst],
          ['IGST', totals.igst], ['CESS', totals.cess], ['Grand Total', totals.total],
        ].map(([label, val]: any) => (
          <div key={label} className="bg-white px-2 py-1 text-center">
            <div className="text-gray-500 uppercase text-[8px] tracking-wide">{label}</div>
            <div className="font-bold text-[#1e8585] tabular-nums text-[11px]">₹{fmt(val)}</div>
          </div>
        ))}
      </div>

      <div className="flex border-b border-[#aebec8] shrink-0 overflow-x-auto">
        {(['b2b', 'cdnr', 'b2cs', 'b2cl', 'nil', 'hsn'] as TabType[]).map(t => {
          const counts: Record<TabType, number> = {
            b2b: b2bRows.length, cdnr: cdnrRows.length, b2cs: b2csRows.length,
            b2cl: b2clRows.length, nil: nilRows.length, hsn: hsnSummary.length,
          };
          const labels: Record<TabType, string> = {
            b2b: 'B2B', cdnr: 'CDNR/CDNUR', b2cs: 'B2CS', b2cl: 'B2CL', nil: 'Nil/Exempt', hsn: 'HSN Summary',
          };
          return (
            <div key={t} className={T(t)} onClick={() => setTab(t)}>
              {labels[t]} [{counts[t]}]
            </div>
          );
        })}
      </div>

      <div className="px-3 py-[3px] bg-[#f4fbfb] border-b border-[#e0ecec] text-[10px] text-gray-500 shrink-0">
        {TAB_HINTS[tab]}
      </div>

      <div className="flex-1 overflow-auto">
        {tab === 'b2b'  && <InvoiceTable rows={b2bRows}  showGstin showPos />}
        {tab === 'cdnr' && <InvoiceTable rows={cdnrRows} showGstin showType showPos />}
        {tab === 'b2cs' && <InvoiceTable rows={b2csRows} showPos />}
        {tab === 'b2cl' && <InvoiceTable rows={b2clRows} showPos />}

        {tab === 'nil' && (
          <table className="w-full border-collapse">
            <thead className="sticky top-0 bg-white z-10">
              <tr>
                <th className={th}>Date</th><th className={th}>Invoice No.</th>
                <th className={th}>Party</th><th className={th}>GSTIN</th>
                <th className={`${th} text-right`}>Exempt Value (₹)</th>
                <th className={th}>Nature</th>
              </tr>
            </thead>
            <tbody>
              {nilRows.length === 0 ? (
                <tr><td colSpan={6} className="text-center text-gray-400 py-8 text-sm">No nil-rated / exempt supplies.</td></tr>
              ) : nilRows.map((r: any, i: number) => (
                <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-[#f8fcfc]'}>
                  <td className={td}>{r.date}</td>
                  <td className={td}>{r.referenceNumber || r.voucherNumber || '—'}</td>
                  <td className={td}>{r.party}</td>
                  <td className={`${td} font-mono text-[10px]`}>{r.gstin || 'Unregistered'}</td>
                  <td className={tR}>{fmt(r.taxable)}</td>
                  <td className={td}><span className="text-[9px] px-1 py-0.5 rounded bg-gray-100 text-gray-600">Nil / Exempt / 0% GST</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {tab === 'hsn' && (
          <table className="w-full border-collapse">
            <thead className="sticky top-0 bg-white z-10">
              <tr>
                <th className={th}>HSN/SAC</th><th className={th}>Description</th>
                <th className={th}>UQC</th><th className={th}>GST %</th>
                <th className={`${th} text-right`}>Qty</th>
                <th className={`${th} text-right`}>Taxable (₹)</th>
                <th className={`${th} text-right`}>CGST (₹)</th>
                <th className={`${th} text-right`}>SGST (₹)</th>
                <th className={`${th} text-right`}>IGST (₹)</th>
                {totals.cess > 0 && <th className={`${th} text-right`}>CESS (₹)</th>}
                <th className={`${th} text-right`}>Total Tax (₹)</th>
              </tr>
            </thead>
            <tbody>
              {hsnSummary.length === 0 ? (
                <tr><td colSpan={11} className="text-center text-gray-400 py-8 text-sm">No HSN data. Add HSN codes to stock items.</td></tr>
              ) : hsnSummary.map((h, i) => (
                <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-[#f8fcfc]'}>
                  <td className={`${td} font-mono text-[10px] font-semibold`}>{h.hsn || '—'}</td>
                  <td className={td}>{h.name}</td>
                  <td className={`${td} uppercase font-mono text-[10px]`}>{h.uqc || 'OTH'}</td>
                  <td className={td}>{h.gstRate}%</td>
                  <td className={tR}>{h.qty.toLocaleString('en-IN')}</td>
                  <td className={tR}>{fmt(h.taxable)}</td>
                  <td className={tR}>{h.cgst > 0 ? fmt(h.cgst) : '—'}</td>
                  <td className={tR}>{h.sgst > 0 ? fmt(h.sgst) : '—'}</td>
                  <td className={tR}>{h.igst > 0 ? fmt(h.igst) : '—'}</td>
                  {totals.cess > 0 && <td className={tR}>{h.cess > 0 ? fmt(h.cess) : '—'}</td>}
                  <td className={`${tR} font-semibold`}>{fmt(h.cgst + h.sgst + h.igst + h.cess)}</td>
                </tr>
              ))}
              {hsnSummary.length > 0 && (
                <tr className="bg-[#f0fafa] font-bold border-t-2 border-[#aebec8]">
                  <td className={th} colSpan={5}>Total</td>
                  <td className={`${th} text-right`}>{fmt(hsnSummary.reduce((s, h) => s + h.taxable, 0))}</td>
                  <td className={`${th} text-right`}>{fmt(hsnSummary.reduce((s, h) => s + h.cgst, 0))}</td>
                  <td className={`${th} text-right`}>{fmt(hsnSummary.reduce((s, h) => s + h.sgst, 0))}</td>
                  <td className={`${th} text-right`}>{fmt(hsnSummary.reduce((s, h) => s + h.igst, 0))}</td>
                  {totals.cess > 0 && <td className={`${th} text-right`}>{fmt(hsnSummary.reduce((s, h) => s + h.cess, 0))}</td>}
                  <td className={`${th} text-right`}>{fmt(hsnSummary.reduce((s, h) => s + h.cgst + h.sgst + h.igst + h.cess, 0))}</td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      <div className="px-3 py-1 bg-gray-50 border-t border-gray-200 text-[9px] text-gray-400 shrink-0">
        ⚠ Auto-computed from vouchers. Verify against GST portal before filing. B2CL threshold ₹2,50,000 per invoice. CDNR = notes to registered; CDNUR = notes to unregistered.
      </div>
    </div>
  );
};
